/**
 * PromptRow — one nudge question in the Activity timeline: who was asked,
 * which question, and whether it is still waiting, was answered (with the
 * option the manager picked) or expired unanswered.
 */
import { BellRing } from 'lucide-react';
import { fullDateTime, relativeTime } from '@/lib/time';
import type { Prompt } from '@/features/pipeline/types';
import { promptAnswerLabel, promptKindLabel, promptState } from './nudge-format';

const STATE_CHIP = {
  open: { label: 'Awaiting reply', chip: 'bg-warning-bg text-warning-text' },
  answered: { label: 'Answered', chip: 'bg-success-bg text-success' },
  expired: { label: 'Expired', chip: 'bg-page text-gray-700' },
} as const;

export function PromptRow({ prompt }: { prompt: Prompt }) {
  const state = promptState(prompt);
  const meta = STATE_CHIP[state];
  const who = prompt.recipient_name?.trim() || prompt.phone_e164 || 'a recipient';
  const sentAt = prompt.sent_at ?? prompt.created_at;
  const answer = promptAnswerLabel(prompt);

  return (
    <div data-testid={`prompt-${prompt.id}`} data-state={state} className="text-[13px] leading-5 text-gray-900">
      <div className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5">
        <BellRing size={12} strokeWidth={2} aria-hidden className="text-gray-500" />
        <span>
          Nudge to <span className="font-medium">{who}</span> — {promptKindLabel(prompt.kind)} question
        </span>
        <span className={`rounded-full px-2 py-0.5 text-[12px] font-medium ${meta.chip}`} data-testid="prompt-state">
          {meta.label}
        </span>
      </div>
      {state === 'answered' && answer && (
        <p className="mt-0.5 text-gray-800">
          Reply: <span className="font-medium">{answer}</span>
        </p>
      )}
      <div className="mt-0.5 text-[12px] text-muted">
        Sent{' '}
        <time dateTime={sentAt} title={fullDateTime(sentAt)}>{relativeTime(sentAt)}</time>
        {state === 'answered' && prompt.answered_at && (
          <>
            <span aria-hidden> · </span>
            answered <time dateTime={prompt.answered_at} title={fullDateTime(prompt.answered_at)}>{relativeTime(prompt.answered_at)}</time>
          </>
        )}
        {state !== 'answered' && prompt.expires_at && (
          <>
            <span aria-hidden> · </span>
            {state === 'expired' ? 'expired' : 'expires'}{' '}
            <time dateTime={prompt.expires_at} title={fullDateTime(prompt.expires_at)}>{fullDateTime(prompt.expires_at)}</time>
          </>
        )}
      </div>
    </div>
  );
}
